import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { GasPump, Path, CurrencyInr, Gauge, TrendUp } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';

const FUEL_EFF_COLUMNS = [
  { key: 'key', label: 'Name' },
  { key: 'trips', label: 'Trips' },
  { key: 'kms', label: 'KMs' },
  { key: 'fills', label: 'Fills' },
  { key: 'fuel', label: 'Fuel (L)' },
  { key: 'cost', label: 'Cost (₹)' },
  { key: 'kmpl', label: 'KM/L' },
  { key: 'cost_per_km', label: 'Cost/KM' },
];

function StatCard({ label, value, unit, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-5 flex items-start gap-4">
      <div className={`w-10 h-10 flex items-center justify-center ${accent || 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
        <Icon size={20} weight="bold" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#666]">{label}</p>
        <p className="text-2xl font-black tracking-tight text-[#0A0A0A] mt-1">
          {value}
          {unit && <span className="text-sm font-medium text-[#666] ml-1">{unit}</span>}
        </p>
      </div>
    </div>
  );
}

const fmt = (v, digits = 2) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
};

export default function FuelEfficiency() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(); monthStart.setDate(1);
  const [fromDate, setFromDate] = useState(monthStart.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today);
  const [groupBy, setGroupBy] = useState('vehicle');
  const [data, setData] = useState({ summary: {}, breakdown: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/fuel-efficiency', {
        params: { from_date: fromDate, to_date: toDate, group_by: groupBy },
      });
      setData(data);
    } catch {
      setData({ summary: {}, breakdown: [] });
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { summary = {}, breakdown = [] } = data;
  const q = search.trim().toLowerCase();
  const rows = q
    ? breakdown.filter(b => (b.key || '').toLowerCase().includes(q))
    : breakdown;

  return (
    <div data-testid="fuel-efficiency-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Fuel Efficiency</h1>
        <p className="text-[#666] text-sm mt-1">KMs travelled vs fuel logged. Based on completed trips and approved fuel records.</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest">From</Label>
          <Input data-testid="fe-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-none" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">To</Label>
          <Input data-testid="fe-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-none" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Group by</Label>
          <div className="flex border border-[#E5E5E5] h-9">
            <button
              type="button"
              data-testid="fe-group-vehicle"
              onClick={() => setGroupBy('vehicle')}
              className={`flex-1 text-xs uppercase tracking-widest ${groupBy === 'vehicle' ? 'bg-[#002FA7] text-white' : 'bg-white text-[#0A0A0A]'}`}
            >Vehicle</button>
            <button
              type="button"
              data-testid="fe-group-pilot"
              onClick={() => setGroupBy('pilot')}
              className={`flex-1 text-xs uppercase tracking-widest border-l border-[#E5E5E5] ${groupBy === 'pilot' ? 'bg-[#002FA7] text-white' : 'bg-white text-[#0A0A0A]'}`}
            >Pilot</button>
          </div>
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs uppercase tracking-widest">Search</Label>
          <Input data-testid="fe-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={groupBy === 'vehicle' ? 'Vehicle number' : 'Pilot name'} className="rounded-none" />
        </div>
        <div>
          <Button data-testid="fe-refresh" onClick={fetchData} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none w-full">
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        <div>
          <ExportButton rows={rows} columns={FUEL_EFF_COLUMNS} filename={`fuel_efficiency_${groupBy}`} testId="export-fuel-efficiency-btn" className="w-full" />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total KMs" value={fmt(summary.total_kms)} unit="km" icon={Path} />
        <StatCard label="Fuel Logged" value={fmt(summary.total_fuel)} unit="L" icon={GasPump} accent="bg-[#FF8C00]/10 text-[#FF8C00]" />
        <StatCard label="Fuel Cost" value={`₹${fmt(summary.total_cost, 0)}`} unit="" icon={CurrencyInr} accent="bg-[#00C853]/10 text-[#00C853]" />
        <StatCard label="Avg Efficiency" value={fmt(summary.avg_kmpl)} unit="km/L" icon={Gauge} accent="bg-[#002FA7]/10 text-[#002FA7]" />
        <StatCard label="Avg Cost/KM" value={summary.avg_cost_per_km != null ? `₹${fmt(summary.avg_cost_per_km)}` : '—'} unit="" icon={TrendUp} accent="bg-[#FF3333]/10 text-[#FF3333]" />
      </div>

      {/* Breakdown table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="font-black tracking-tight text-[#0A0A0A]">{groupBy === 'vehicle' ? 'By Vehicle' : 'By Pilot'}</h2>
          <span className="text-xs text-[#666]">{rows.length} {rows.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">{groupBy === 'vehicle' ? 'Vehicle' : 'Pilot'}</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Trips</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">KMs</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Fills</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Fuel (L)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Cost (₹)</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">KM/L</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Cost/KM</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.key} className={i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30'} data-testid={`fe-row-${r.key}`}>
                <TableCell className="font-medium">{r.key}</TableCell>
                <TableCell className="text-right">{r.trips}</TableCell>
                <TableCell className="text-right">{fmt(r.kms)}</TableCell>
                <TableCell className="text-right">{r.fills}</TableCell>
                <TableCell className="text-right">{fmt(r.fuel)}</TableCell>
                <TableCell className="text-right">₹{fmt(r.cost, 0)}</TableCell>
                <TableCell className="text-right font-semibold">
                  {r.kmpl != null
                    ? <span className={r.kmpl >= (summary.avg_kmpl || 0) ? 'text-[#00C853]' : 'text-[#FF3333]'}>{fmt(r.kmpl)}</span>
                    : <span className="text-[#999]">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {r.cost_per_km != null ? `₹${fmt(r.cost_per_km)}` : <span className="text-[#999]">—</span>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-[#666] py-8">
                {loading ? 'Loading...' : 'No data for the selected range'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-[10px] text-[#666]">
        KMs per trip uses Manual KM &gt; Google KM &gt; (End − Start) as fallback. Efficiency (KM/L) shown only when approved fuel logs exist for the period.
      </p>
    </div>
  );
}
